'use client'

import type {
    ClientChromeCopy,
    FormatCopy,
    MenuCopy,
    MoreMenuCopy,
    NavCopy,
    SearchCopy,
} from '@/app/(site)/chrome-copy'
import { createContext, useContext, type ReactNode } from 'react'

/**
 * Carries server-resolved UI strings to client components that sit too deep to
 * reach with props.
 *
 * The global nav is the case this exists for: it renders inside the search bar,
 * inside HeaderInit, several levels below the nearest Server Component. Where a
 * Server Component is the direct parent -- the search header, the GIF detail
 * sidebar -- pass the strings as props instead and leave this alone.
 *
 * The defaults are English rather than empty. A client component that renders
 * outside the provider gets readable copy instead of blank labels.
 */
const SiteChromeCopyContext = createContext<ClientChromeCopy | undefined>(undefined)

const FALLBACK_NAV: NavCopy = {
    reactions: 'Reactions',
    favorites: 'Favorites',
    categories: 'Categories',
    create: 'Create',
    advertise: 'Advertise',
    login: 'Log In',
    myChannel: 'My Channel',
    more: 'More',
}

const FALLBACK_MENUS: MenuCopy = {
    dashboard: 'Dashboard',
    upgradeAccount: 'Upgrade Account',
    collections: 'Collections',
    superCut: 'Super Cut',
    settings: 'Settings',
    logOut: 'Log Out',
    upload: 'Upload',
    create: 'Create',
}

const FALLBACK_MORE_MENU: MoreMenuCopy = {
    about: 'About',
    team: 'Team',
    developers: 'Developers',
    applyAsABrand: 'Apply as a Brand',
    faq: 'FAQ',
    support: 'Support',
    transparency: 'Transparency',
    jobs: 'Jobs',
    artists: 'Artists',
    artistDirectory: 'Artist Directory',
    hireAnArtist: 'Hire a GIF Artist',
}

// GIPHY uses these as format names rather than common nouns, so they read the
// same in both languages -- but they still come from the catalog, because that
// is a translation decision and not something to hard-code at the call site.
const FALLBACK_FORMATS: FormatCopy = {
    gifs: 'GIFs',
    stickers: 'Stickers',
    clips: 'Clips',
}

const FALLBACK_SEARCH: SearchCopy = {
    sort: 'Sort',
    filter: 'Filter',
    report: 'Report',
}

export const useNavCopy = (): NavCopy => useContext(SiteChromeCopyContext)?.nav ?? FALLBACK_NAV

export const useMenuCopy = (): MenuCopy => useContext(SiteChromeCopyContext)?.menus ?? FALLBACK_MENUS

export const useMoreMenuCopy = (): MoreMenuCopy => useContext(SiteChromeCopyContext)?.moreMenu ?? FALLBACK_MORE_MENU

export const useFormatCopy = (): FormatCopy => useContext(SiteChromeCopyContext)?.formats ?? FALLBACK_FORMATS

export const useSearchCopy = (): SearchCopy => useContext(SiteChromeCopyContext)?.search ?? FALLBACK_SEARCH

export const SiteChromeCopyProvider = ({ copy, children }: { copy: ClientChromeCopy; children: ReactNode }) => (
    <SiteChromeCopyContext.Provider value={copy}>{children}</SiteChromeCopyContext.Provider>
)
